/**
 * MTFConfluencePanel — Multi-Timeframe Signal Confluence Grid.
 * Shows the latest UT Bot signal per symbol × timeframe combination.
 * Highlights rows where signals align across multiple timeframes.
 */

import { memo, useMemo } from 'react';
import { useTerminalStore } from '../../stores/terminal.store.js';
import type { TimeframeKey } from '@arc/shared';

const TIMEFRAMES: TimeframeKey[] = ['15m', '1h', '4h', '1D'];

const TF_LABELS: Record<TimeframeKey, string> = {
  '1m': '1m', '5m': '5m', '15m': '15m', '30m': '30m',
  '1h': '1h', '4h': '4h', '1D': 'D', '1W': 'W',
};

// ─── Signal cell ──────────────────────────────────────────────────────────────

function SignalCell({ signal }: { signal: 'BUY' | 'SELL' | null }) {
  if (!signal) {
    return (
      <div className="flex items-center justify-center h-full">
        <span className="font-mono text-[11px] opacity-90">—</span>
      </div>
    );
  }

  const isBuy = signal === 'BUY';
  return (
    <div
      className={`flex items-center justify-center h-full font-mono text-[11px] font-bold tracking-wider transition-all ${
        isBuy ? 'text-buy' : 'text-sell'
      }`}
      style={{
        background: isBuy ? 'rgba(0,255,136,0.05)' : 'rgba(255,60,60,0.05)',
      }}
    >
      {isBuy ? '▲' : '▼'}
    </div>
  );
}

// ─── Confluence score badge ───────────────────────────────────────────────────

function ConfluenceBadge({ score, total }: { score: number; total: number }) {
  if (score === 0) return null;
  const pct     = score / total;
  const isBull  = score > 0;
  const abs     = Math.abs(score);

  return (
    <div
      className="flex items-center justify-center font-mono text-[11px] font-bold px-1 border"
      style={{
        color:       isBull ? 'var(--color-buy)' : 'var(--color-sell)',
        borderColor: isBull ? 'rgba(0,255,136,0.3)' : 'rgba(255,60,60,0.3)',
        background:  isBull ? 'rgba(0,255,136,0.08)' : 'rgba(255,60,60,0.08)',
        opacity:     0.4 + pct * 0.6,
      }}
    >
      {isBull ? '+' : ''}{abs}/{total}
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

export const MTFConfluencePanel = memo(function MTFConfluencePanel() {
  const signals  = useTerminalStore(s => s.signals);
  const tokens   = useTerminalStore(s => s.tokens);
  const prices   = useTerminalStore(s => s.prices);

  // For each symbol, get latest signal per timeframe
  const rows = useMemo(() => {
    return tokens
      .filter(t => prices[t.symbol]) // only tokens with price data
      .map(token => {
        const symbol = token.symbol;
        const tfSignals: Partial<Record<TimeframeKey, 'BUY' | 'SELL'>> = {};
        let score = 0; // +1 per BUY, -1 per SELL

        for (const tf of TIMEFRAMES) {
          const key  = `${symbol}:${tf}`;
          const list = signals[key];
          const latest = list?.[0]; // sorted newest first
          if (latest) {
            tfSignals[tf] = latest.type;
            score += latest.type === 'BUY' ? 1 : -1;
          }
        }

        const hasSignals = Object.keys(tfSignals).length > 0;
        return { symbol, tfSignals, score, hasSignals, price: prices[symbol] };
      })
      // Sort: strongest confluence first
      .sort((a, b) => Math.abs(b.score) - Math.abs(a.score));
  }, [signals, tokens, prices]);

  const visibleRows = rows.filter(r => r.hasSignals);
  const emptyRows   = rows.filter(r => !r.hasSignals);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Column headers */}
      <div
        className="grid flex-shrink-0 border-b border-hud"
        style={{ gridTemplateColumns: '1fr 36px 36px 36px 36px 40px' }}
      >
        <div className="font-mono text-[11px] px-2 py-1 opacity-90 tracking-widest">SYMBOL</div>
        {TIMEFRAMES.map(tf => (
          <div key={tf} className="font-mono text-[11px] py-1 text-center opacity-90 tracking-widest">
            {TF_LABELS[tf]}
          </div>
        ))}
        <div className="font-mono text-[11px] py-1 text-center opacity-90 tracking-widest">CON</div>
      </div>

      {/* Rows */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {visibleRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 opacity-30">
            <div className="text-[24px]">⬡</div>
            <span className="font-mono text-[11px] tracking-widest">AWAITING SIGNALS</span>
            <span className="font-mono text-[11px] opacity-90">Confluence populates as signals arrive</span>
          </div>
        ) : (
          <>
            {visibleRows.map(row => (
              <div
                key={row.symbol}
                className="grid border-b border-hud/30 hover:bg-white/[0.02] transition-colors"
                style={{
                  gridTemplateColumns: '1fr 36px 36px 36px 36px 40px',
                  minHeight: '32px',
                  borderLeftWidth: '2px',
                  borderLeftColor: row.score > 0
                    ? `rgba(0,255,136,${Math.min(0.6, Math.abs(row.score) * 0.2)})`
                    : row.score < 0
                      ? `rgba(255,60,60,${Math.min(0.6, Math.abs(row.score) * 0.2)})`
                      : 'transparent',
                }}
              >
                {/* Symbol + price */}
                <div className="flex flex-col justify-center px-2 py-1">
                  <span className="font-orb font-bold text-[11px]" style={{ color: 'var(--color-bright)' }}>
                    {row.symbol}
                  </span>
                  {row.price && (
                    <span className={`font-mono text-[11px] ${(row.price.change24h ?? 0) >= 0 ? 'text-buy' : 'text-sell'}`}>
                      ${row.price.priceUsd > 100
                        ? row.price.priceUsd.toFixed(0)
                        : row.price.priceUsd.toFixed(4)}
                    </span>
                  )}
                </div>

                {/* Signal cells per timeframe */}
                {TIMEFRAMES.map(tf => (
                  <SignalCell key={tf} signal={row.tfSignals[tf] ?? null} />
                ))}

                {/* Confluence score */}
                <div className="flex items-center justify-center px-1">
                  <ConfluenceBadge score={row.score} total={TIMEFRAMES.length} />
                </div>
              </div>
            ))}

            {/* Tokens without signals (grayed out) */}
            {emptyRows.slice(0, 5).map(row => (
              <div
                key={row.symbol}
                className="grid border-b border-hud/20 opacity-25"
                style={{ gridTemplateColumns: '1fr 36px 36px 36px 36px 40px', minHeight: '28px' }}
              >
                <div className="flex items-center px-2">
                  <span className="font-orb text-[11px]" style={{ color: 'var(--color-muted)' }}>
                    {row.symbol}
                  </span>
                </div>
                {TIMEFRAMES.map(tf => (
                  <div key={tf} className="flex items-center justify-center">
                    <span className="font-mono text-[11px] opacity-90">—</span>
                  </div>
                ))}
                <div />
              </div>
            ))}
          </>
        )}
      </div>

      {/* Footer: confluence legend */}
      <div className="flex-shrink-0 border-t border-hud px-2 py-1 flex items-center gap-3">
        <span className="font-mono text-[11px] opacity-90 tracking-widest">LEGEND:</span>
        <span className="font-mono text-[11px] text-buy opacity-90">▲ BUY</span>
        <span className="font-mono text-[11px] text-sell opacity-90">▼ SELL</span>
        <span className="font-mono text-[11px] opacity-90">— NO SIGNAL</span>
        <span className="font-mono text-[11px] opacity-90 ml-auto">CON = {TIMEFRAMES.length} TF</span>
      </div>
    </div>
  );
});
